<template>
  <div class="submenu" :style="position">
    <div v-for="(item, index) in menuItems" :key="index">
      <div v-if="item.type === 'divider'" class="sub-menu-divider"></div>
      <div
        v-else
        class="sub-menu-item"
        :class="{ 'has-submenu': item.children }"
        @click.stop="$emit('item-click', item)"
        @mouseover="handleMouseOver"
      >
        <span>
          {{ item.text }}
          <span v-if="item.children" class="menu-arrow">▶</span>
        </span>
        <sub-menu
          v-if="item.children && isHovered"
          :menu-items="item.children"
          :parent-rect="subMenuParentRect"
          @item-click="$emit('item-click', $event)"
        ></sub-menu>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  // 使用 name 属性实现组件的递归引用
  name: 'subMenu', 
  props: {
    menuItems: {
      type: Array,
      default: () => []
    },
    // 【新增】接收父级菜单项的边界矩形（用于定位判断）
    parentRect: {
      type: DOMRect,
      default: null 
    }
  },
  components: {
    // 递归注册自身
    // subMenu: () => import('./SubMenu.vue') // 如果是异步加载
    // 在 Vue 2 中，如果文件名和 name 一致，通常可以直接使用 name
  },
  data() {
    return {
      position: {}, // 【新增】用于动态设置 left 和 top 的 style 对象
      isHovered: false, // 【新增】用于控制子菜单的显示/隐藏（防止闪烁）
      subMenuParentRect: null, // 【新增】存储当前悬停项的 rect，用于传递给下一级菜单
    };
  },
  watch: {
      // 当菜单项变化或父级边界变化时，重新计算位置
      menuItems() {
          this.$nextTick(this.calculatePosition);
      },
      parentRect() {
          this.$nextTick(this.calculatePosition);
      }
  },
  mounted() {
    // 确保 DOM 渲染后进行位置计算
    this.$nextTick(this.calculatePosition);
  },
  methods: {
    handleMouseOver(event) {
        // 标记悬停状态，并获取当前菜单项的边界
        this.isHovered = true;
        this.subMenuParentRect = event.currentTarget.getBoundingClientRect();
    },
    handleMouseLeave() {
        // 可选：添加延迟隐藏逻辑，防止快速移动时闪烁
        // this.isHovered = false; 
    },
    /**
     * @description 计算并设置菜单的展开位置，防止超出视口
     */
    calculatePosition() {
      // 只有非顶级菜单才需要调整位置（顶级菜单由主组件定位）
      if (!this.parentRect) {
          // 顶级菜单（最外层）的定位不需要防溢出，保持默认即可
          this.position = { left: this.position.left || '100%', top: this.position.top || '0' };
          return;
      }

      const menuEl = this.$el;
      if (!menuEl) return;

      const parentRect = this.parentRect;
      
      // 1. 获取子菜单的实际尺寸
      // 注意：getBoundingClientRect() 可能会因为 transition 动画而返回旧值
      const menuRect = menuEl.getBoundingClientRect();
      const menuW = menuRect.width || 180; // 使用默认值防止计算错误
      const menuH = menuRect.height || 100;
      
      // 2. 获取视口尺寸
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      
      // 3. 计算默认（向右）展开时的边界
      // 菜单的左边界：父级菜单项的右边界（相对于视口左侧）
      const potentialLeftX = parentRect.right;
      const potentialRightBoundary = potentialLeftX + menuW;
      
      let finalLeft;
      let finalTop = 0; // 垂直位置默认与父级菜单项顶部对齐
      
      // --- 水平方向判断（X轴） ---
      if (potentialRightBoundary > viewportW) {
        // 超出视口，改为向左展开
        // 新的 left 值 = -菜单宽度 (相对于父级 menu-item)
        finalLeft = `-${menuW}px`;
      } else {
        // 未超出，保持默认向右展开
        finalLeft = '100%'; 
      }
      
      // --- 垂直方向判断（Y轴） ---
      // 检查菜单底部是否超出视口
      // 菜单相对于视口顶部的 Y 坐标 = 父菜单项的顶部 Y 坐标 + 菜单高度
      const potentialBottomY = parentRect.top + menuH;
      
      if (potentialBottomY > viewportH) {
          // 菜单底部超出视口，向上移动
          // 新的 top 值 = 父级菜单项的高度 - 菜单高度 (相对于父级 menu-item)
          // 注意：需要确保顶部不会超出屏幕
          
          // 菜单相对于父级 menu-item 顶部的偏移量
          const parentHeight = parentRect.height;
          finalTop = -(menuH - parentHeight) + 'px';
          
          // 额外的检查：如果向上移动后，菜单顶部超出屏幕顶部，则固定到屏幕顶部
          // (这个检查可能需要在更上层组件处理，这里保持相对于父级的简单调整)
      } else {
          finalTop = '0';
      }

      // 4. 应用最终修正后的位置
      this.position = { 
          left: finalLeft, 
          top: finalTop 
      };
    }
  }
};
</script>

<style scoped lang="scss">

.submenu {
  position: absolute;
  /* 移除固定的 left/top，改为 JS 动态计算 */
  background-color: white;
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  padding: 6px 0;
  z-index: 1000; /* 确保层级高 */
  
  /* 保持悬停显示逻辑的过渡效果 */
  opacity: 0;
  visibility: hidden;
  transform: translateX(-10px);
  transition: all 0.15s;
  max-height: 350px;
  overflow: auto;
}

/* 核心 CSS 逻辑：当父级菜单项悬停时，显示子菜单 */
.context-menu-item:hover > .submenu {
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
}

.sub-menu-item {
  padding: 8px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative; /* 核心：子菜单的定位基准 */
  transition: background-color 0.15s;
  color: #000000;
}

.sub-menu-item:hover {
  background-color: #F5F7FA;
  color: #1942FF;
}

.menu-arrow {
    margin-left: 10px;
    font-size: 12px;
}

.sub-menu-divider {
  height: 1px;
  background-color: #eee;
  margin: 5px 0;
}
/* ... 其他样式保持不变 ... */
</style>